/**
 * AccessRequest — one organization's request to run its events on Revent, at
 * `workspaces/revia/accessRequests/{requestId}`, filed from the landing's
 * request form and decided by Revia staff in the backoffice.
 *
 * The id is `platformHmac(email)`, a keyed hash under the platform data key:
 * one address holds one request, and a repeated request finds the first by
 * key.
 *
 * The address is stored in clear beside that key. Revia mails the requester
 * — the "received" mail when the request is filed or reopened, the grant
 * mail when staff approve or a granted address whose organization still
 * exists asks again — and a hash cannot be mailed. The address lives only
 * as long as the request it answers: an unresolved request carries the `expiresAt` TTL clock.
 *
 * Approving founds the organization with an `owner` invitation for that
 * address. The person proves the address only later, at the organization's
 * sign-in, where the one-time code reaches that inbox and the invitation
 * admits it; accepting it makes them the owner. A request filed for someone
 * else's address therefore admits nobody but that address's owner.
 *
 * ```mermaid
 * stateDiagram-v2
 *   [*] --> pending: requestAccess, new address
 *   pending --> later: setAccessRequestStatus
 *   later --> pending: setAccessRequestStatus, or requestAccess (reopened)
 *   pending --> granted: approveAccessRequest
 *   later --> granted: approveAccessRequest
 *   granted --> pending: requestAccess, its organization gone
 *   pending --> [*]: expiresAt
 *   later --> [*]: expiresAt
 * ```
 *
 * Fields justified:
 *   businessType  — (pending, later) the kind of events the organization
 *                   runs; staff triage by it.
 *   organization  — the organization they want to found, validated as a
 *                   founded organization's name is; approve founds it under
 *                   this name, or under the one staff chose instead, the
 *                   source of its id. A granted request carries the name
 *                   the organization was founded under.
 *   phone         — (pending, later) a number staff call before deciding.
 *                   Both are dropped at grant: once staff decided, nothing
 *                   reads them.
 *   email         — the address both mails go to, trimmed and lower-cased:
 *                   the id is the hash of this one spelling.
 *   locale        — the language the landing was showing; both mails are
 *                   written in it.
 *   createdAt     — when the request was first filed; the backoffice list's
 *                   order. A reopen keeps it.
 *   status        — `pending` awaiting a decision, `later` set aside by
 *                   staff, `granted` approved with its organization founded.
 *   orgId         — (granted) the organization approve founded; the grant
 *                   mail's sign-in link opens it, and the request is deleted
 *                   with it.
 *   reviewedAt    — when staff last moved the request; required on `later`
 *                   and `granted`, present on `pending` exactly when staff
 *                   moved it back there.
 *   reviewedBy    — the uid of the staff member who made that move; present
 *                   exactly when `reviewedAt` is. A reopen by the requester
 *                   is no staff move and clears both.
 *   expiresAt     — the TTL clock, `UNRESOLVED_RETENTION_MONTHS` past the
 *                   requester's last submission: filing the request, or
 *                   reopening it from `later` with freshly submitted data.
 *                   A staff move between `pending` and `later` keeps it,
 *                   so no staff action extends retention.
 *                   Required on `pending` and `later`; refused on `granted`,
 *                   so the writer that grants deletes the clock it inherits
 *                   rather than leaving Firestore to delete an approved
 *                   request. `firestore.indexes.json` declares it a TTL field
 *                   on the `accessRequests` collection id.
 *   grantMailedAt — (granted) when a grant mail was last queued, stamped in
 *                   the transaction that queues it. A granted address that
 *                   asks again is mailed again only once
 *                   `GRANT_MAIL_INTERVAL_HOURS` have passed since it.
 *
 * Granted requests do not expire on a clock: a deleted granted request would
 * let the same address re-enter the queue as a new pending request after its
 * organization exists, and would lose the address the grant mail is resent
 * to. A granted request is deleted with the organization founded from it
 * instead.
 *
 * Excluded:
 *   id               — the doc id.
 *   requester name, message — the form asks for the organization and its
 *                      contact only.
 *   refused status   — no flow refuses a request; `later` is the most staff
 *                      say, and an unanswered request expires.
 *
 * Access: read and list for Revia staff (`STAFF_PERMISSION` held in
 * `workspaces/revia`) signed in with a second factor (the `mfa` claim), as
 * Revent's permission-gated member reads are. `create`, `approve` and
 * `setStatus` are server-only, through the `requestAccess`,
 * `approveAccessRequest` and `setAccessRequestStatus` callables.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { FoundOrganization, Order, Workspace } from "@momo/types";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { REVIA_ORG_ID, STAFF_PERMISSION } from "./staff.const.ts";

// How long a pending or later request is kept before the TTL deletes it.
export const UNRESOLVED_RETENTION_MONTHS = 6;

// The least time between two grant mails to one address.
export const GRANT_MAIL_INTERVAL_HOURS = 24;

// The request id: platformHmac(email), 64 lowercase hex characters.
export const AccessRequestId = Workspace.Sha256.describe(
	"the keyed hash of the requester's email address",
);
export type AccessRequestId = z.infer<typeof AccessRequestId>;

export const BusinessType = z
	.enum(["theatre", "concerts", "club", "festival", "comedy", "sports", "other"])
	.describe("the kind of events the organization runs");
export type BusinessType = z.infer<typeof BusinessType>;

export const AccessRequestStatus = z.enum(["pending", "granted", "later"]);
export type AccessRequestStatus = z.infer<typeof AccessRequestStatus>;

export const AccessRequestOrganizationSchema =
	FoundOrganization.OrganizationNameSchema.max(200).describe(
		"the organization the requester wants to found",
	);

// Kept as typed, with or without a country code: staff dial it and nothing
// parses it, so normalizing to E.164 would need a country the form does not
// ask for. E.164 caps a number at 15 digits; none dialable has fewer than 5.
export const AccessRequestPhoneSchema = z
	.string()
	.trim()
	.max(32)
	.regex(/^\+?[0-9 ()./-]+$/)
	.refine((value) => {
		const digits = value.replace(/\D/g, "").length;
		return digits >= 5 && digits <= 15;
	}, "between 5 and 15 digits")
	.describe("the phone number staff can reach the requester on");

export const AccessRequestEmailSchema = z
	.string()
	.trim()
	.toLowerCase()
	.pipe(z.email().max(320))
	.describe("the address that will found the organization");

export const AccessRequestLocaleSchema = Order.LocaleSchema.describe(
	"the language the requester read the landing in",
);

const present = <T>() => z.custom<T>((value) => value != null);

const AccessRequestBase = z.object({
	businessType: BusinessType,
	organization: AccessRequestOrganizationSchema,
	phone: AccessRequestPhoneSchema,
	email: AccessRequestEmailSchema,
	locale: AccessRequestLocaleSchema,
	createdAt: present<Timestamp>(),
});

const reviewedAt = present<Timestamp>().describe(
	"when staff last moved the request",
);
const reviewedBy = Workspace.Id.describe(
	"the uid of the staff member who last moved the request",
);
const expiresAt = present<Timestamp>().describe(
	"when the unresolved request is deleted",
);

export const AccessRequestPendingSchema = AccessRequestBase.extend({
	status: z.literal("pending").describe("awaiting a staff decision"),
	reviewedAt: reviewedAt.nullish(),
	reviewedBy: reviewedBy.nullish(),
	expiresAt,
})
	.strict()
	.refine(
		(request) => (request.reviewedAt == null) === (request.reviewedBy == null),
		{ message: "reviewedAt and reviewedBy are set together" },
	);

export const AccessRequestLaterSchema = AccessRequestBase.extend({
	status: z.literal("later").describe("set aside by staff"),
	reviewedAt,
	reviewedBy,
	expiresAt,
}).strict();

export const AccessRequestGrantedSchema = AccessRequestBase.omit({
	businessType: true,
	phone: true,
}).extend({
	status: z
		.literal("granted")
		.describe("approved; the organization is founded"),
	orgId: Workspace.Id.describe("the organization approving founded"),
	reviewedAt,
	reviewedBy,
	grantMailedAt: present<Timestamp>()
		.describe("when the grant mail last went out")
		.nullish(),
}).strict();

export const AccessRequestSchema = z.discriminatedUnion("status", [
	AccessRequestPendingSchema,
	AccessRequestLaterSchema,
	AccessRequestGrantedSchema,
]);
export type AccessRequest = z.infer<typeof AccessRequestSchema>;

const staff = { workspace: REVIA_ORG_ID };

export const AccessRequest = entity<Workspace.Actor>()({
	schema: AccessRequestSchema,
	actions: ["read", "list", "create", "approve", "setStatus"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
		can("create")
			.when(() => and())
			.requiresHandler("requestAccess"),
		can("approve")
			.when(() => and())
			.requiresHandler("approveAccessRequest"),
		can("setStatus")
			.when(() => and())
			.requiresHandler("setAccessRequestStatus"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: `workspaces/${REVIA_ORG_ID}/accessRequests/{requestId}`,
		}),
	);
